
import { useState, useEffect, useCallback, useRef } from 'react';
import { dataCentralManager, DashboardData, ChatListData } from '@/core/DataCentralManager';
import { ConnectionsResponse } from '@/core/services/ConnectionsService';
import { SortOption, sortProfiles } from '@/utils/profileUtils';
import { useAuth } from '@/hooks/useAuth';
import { useProfile } from '@/contexts/ProfileContext';
import { ProfileData } from '@/types/profile';
import { debugLogger } from '@/utils/debugLogger';

interface UseDataCentralOptions {
  autoFetch?: boolean;
  sortBy?: SortOption;
  dataType: 'feed' | 'chats' | 'connections';
}

export const useDataCentral = ({ 
  autoFetch = true, 
  sortBy = 'recent',
  dataType 
}: UseDataCentralOptions) => {
  const { user } = useAuth();
  const { profile: userProfile } = useProfile();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(false);
  
  const subscriberIdRef = useRef<string>(crypto.randomUUID());
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const isFetchingRef = useRef<boolean>(false);

  // Initialize manager
  useEffect(() => {
    if (user?.id && !initialized) {
      dataCentralManager.initialize(user.id);
      setInitialized(true);
    }
  }, [user?.id, initialized]);

  // Fetch data with stable dependencies
  const fetchData = useCallback(async (forceRefresh = false) => {
    if (!initialized || isFetchingRef.current) {
      return;
    }
    
    try {
      isFetchingRef.current = true;
      setError(null);
      setLoading(true);
      
      debugLogger.debug('useDataCentral', `Fetching ${dataType} data with forceRefresh:`, forceRefresh);
      
      let result;
      switch (dataType) {
        case 'feed':
          // Always use 'recent' for backend call, sorting will be done in frontend
          result = await dataCentralManager.getFeedData('recent', forceRefresh);
          break;
        case 'chats':
          result = await dataCentralManager.getChatListData(forceRefresh);
          break;
        case 'connections':
          if (!userProfile) {
            debugLogger.warn('useDataCentral', 'User profile not loaded yet, skipping connections fetch');
            return;
          }
          result = await dataCentralManager.getConnectionsData({
            role: userProfile.role,
            hidden: userProfile.hidden || false,
            onboarding_completed: userProfile.onboarding_completed || false
          }, forceRefresh);
          break;
        default:
          throw new Error('Tipo de dados inválido');
      }
      
      debugLogger.debug('useDataCentral', `${dataType} data fetched:`, result);
      setData(result);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao carregar dados';
      debugLogger.error('useDataCentral', `Error fetching ${dataType} data:`, err);
      setError(errorMessage);
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, [initialized, dataType, userProfile]);

  // Subscriber for updates
  useEffect(() => {
    if (!initialized) return;
    
    const subscriber = {
      id: subscriberIdRef.current,
      onDataUpdate: (updateData: any) => {
        debugLogger.debug('useDataCentral', `Received update for ${dataType}:`, updateData);
        if (updateData.type === dataType) {
          setData(updateData.data);
        }
      },
      onError: (errorMessage: string) => {
        debugLogger.error('useDataCentral', `Subscriber error for ${dataType}:`, errorMessage);
        setError(errorMessage);
      }
    };
    
    unsubscribeRef.current = dataCentralManager.subscribe(subscriber.id, subscriber);
    
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [initialized, dataType]);

  // Auto-fetch
  useEffect(() => {
    if (initialized && autoFetch && !isFetchingRef.current) {
      const shouldForceRefresh = dataType === 'chats' || dataType === 'connections';
      debugLogger.debug('useDataCentral', `Auto-fetching ${dataType} with force refresh:`, shouldForceRefresh);
      fetchData(shouldForceRefresh);
    }
  }, [initialized, autoFetch, dataType, fetchData]);

  // Feed-specific functions with frontend sorting
  const getProfiles = useCallback((sortOption: SortOption = sortBy): ProfileData[] => {
    if (dataType !== 'feed' || !data?.feedProfiles) return [];
    
    const profiles = data.feedProfiles;
    const userProfile = data.userProfile;
    
    // Apply frontend sorting using the sortProfiles utility
    return sortProfiles(
      profiles, 
      sortOption, 
      userProfile?.latitude, 
      userProfile?.longitude
    );
  }, [data, sortBy, dataType]);

  const updateConnectionStatus = useCallback((profileId: string, status: string) => {
    if (dataType === 'feed') {
      dataCentralManager.updateConnectionStatus(profileId, status);
    }
  }, [dataType]);

  const getConnectionStatus = useCallback((profileId: string): string => {
    return dataCentralManager.getConnectionStatus(profileId);
  }, []);

  // General functions
  const refreshData = useCallback(() => {
    debugLogger.debug('useDataCentral', `Manually refreshing ${dataType} data`);
    fetchData(true);
  }, [fetchData, dataType]);

  const updatePresence = useCallback((activityType: string = 'general') => {
    dataCentralManager.updateActivity(activityType);
  }, []);

  return {
    // General data
    data,
    loading,
    error,
    
    // Feed-specific data
    ...(dataType === 'feed' && data ? {
      userProfile: data.userProfile || null,
      onboardingStatus: data.onboardingStatus || { completed: false, current_step: 0 },
      connectionStats: data.connectionStats || { sent_count: 0, received_count: 0, matched_count: 0 },
      notificationCounts: data.notificationCounts || { unread_count: 0, total_count: 0 },
      getProfiles,
      updateConnectionStatus,
      getConnectionStatus,
    } : {}),
    
    // Chat-specific data
    ...(dataType === 'chats' && data ? {
      conversations: data.conversations || [],
      totalCount: data.totalCount || 0,
    } : {}),
    
    // Connections-specific data
    ...(dataType === 'connections' && data ? {
      sentConnections: data.sent || [],
      receivedConnections: data.received || [],
      matchedConnections: data.matched || [],
    } : {}),
    
    // General functions
    refreshData,
    updatePresence,
    
    // Cache info for debug
    cacheInfo: dataCentralManager.getCacheInfo()
  };
};
